// Sprint 4c-4 D.1 — Flexpfad-Block (Co-Creator-Output).
// Sprint 6 BLOCKER 2 — Word-Count 150-200 Wörter (Soft-Warning bei 180+).
// Sprint 6 HOCH 9 — „Was ist das?"-Tooltip + Erklärung („Mehr erfahren?").

import React, { useState } from "react";
import { Copy, Check, HelpCircle, ChevronUp, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { ROLE_COLORS } from "@/lib/focusRoles";

const FOKUSTYP_LABEL = { hund: "Hund", biene: "Biene", eule: "Eule", katze: "Katze" };

// Sprint 6 HOCH 9 AC #2 + #3 — Erklär-Text pro Fokustyp.
const BRUECKENSATZ_EXPLAINER = {
  katze:
    "Wenn du mit einer Katze sprichst, hilft dir der Flexpfad, ihre Lust an Freiheit zu erkennen — und nicht durch \u201EDu musst …\u201C abzuprallen.",
  eule:
    "Wenn du mit einer Eule sprichst, hilft dir der Flexpfad, den Sinn zuerst zu zeigen — bevor du um etwas bittest.",
  biene:
    "Wenn du mit einer Biene sprichst, hilft dir der Flexpfad, Klarheit und konkrete Schritte vorzuschlagen — statt vage Pläne.",
  hund:
    "Wenn du mit einem Hund sprichst, hilft dir der Flexpfad, die Beziehung zuerst zu sichern — bevor du in die Sache gehst.",
};

export function BrueckensatzBlock({ bridge, modus, testid = "bruecke-block" }) {
  const [copied, setCopied] = useState(false);
  const [explainerOpen, setExplainerOpen] = useState(false);
  if (!bridge?.text) return null;

  const saetze = bridge.saetze || [];
  const wordCount = bridge.word_count || 0;
  // Sprint 6 BLOCKER 2 — Soft-Limit ist 180 Wörter, Hard-Limit 250.
  const softLimit = bridge.word_count_soft_limit || 180;
  const hardLimit = bridge.word_count_hard_limit || 250;
  const softWarning = bridge.soft_warning ?? wordCount > softLimit;
  const climaxColor = ROLE_COLORS.gegenueber.hex; // #DC2626 Crimson
  const climaxFt = bridge.climax_anker;
  const climaxLabel = climaxFt ? FOKUSTYP_LABEL[climaxFt] : null;
  const explainerText =
    (climaxFt && BRUECKENSATZ_EXPLAINER[climaxFt]) ||
    BRUECKENSATZ_EXPLAINER.katze;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(bridge.text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* noop */
    }
  };

  return (
    <section
      data-testid={testid}
      className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm dark:bg-slate-900 dark:border-slate-700"
      style={{ borderTopColor: ROLE_COLORS.primary.hex, borderTopWidth: 4 }}
    >
      <div className="flex items-baseline justify-between mb-3 gap-2">
        <div className="flex items-center gap-2">
          <p className="text-xs font-bold uppercase tracking-widest text-stone-500 dark:text-slate-400">
            Dein Flexpfad
          </p>
          {/* Sprint 6 HOCH 9 AC #1 — „Was ist das?"-Toggle */}
          <button
            type="button"
            onClick={() => setExplainerOpen((v) => !v)}
            data-testid={`${testid}-explainer-toggle`}
            aria-expanded={explainerOpen}
            aria-label="Was ist der Flexpfad?"
            className="inline-flex items-center gap-1 text-xs font-semibold text-stone-500 hover:text-stone-800 transition-colors dark:text-slate-400 dark:hover:text-slate-100"
          >
            <HelpCircle className="w-3.5 h-3.5" />
            Was ist das?
            {explainerOpen ? (
              <ChevronUp className="w-3 h-3" />
            ) : (
              <ChevronDown className="w-3 h-3" />
            )}
          </button>
        </div>
        <span
          data-testid="modus-badge"
          className="text-xs text-stone-500 whitespace-nowrap dark:text-slate-400"
        >
          {modus === "praezise" ? "Präzise Ansprache" : "Quick-Pfad"}
        </span>
      </div>

      {/* Sprint 6 HOCH 9 AC #2-4 — Inline-Erklärung (collapsible) */}
      {explainerOpen && (
        <div
          data-testid={`${testid}-explainer-body`}
          className="mb-4 rounded-xl border border-stone-200 bg-stone-50/80 p-4 text-sm text-stone-700 leading-relaxed dark:border-slate-700 dark:bg-slate-800/70 dark:text-slate-300"
        >
          <p>
            Der <strong>Flexpfad</strong> hilft dir,
            {climaxLabel ? <> die <strong>{climaxLabel}</strong> in deinem Gegenüber </> : <> dein Gegenüber </>}
            besser zu verstehen — und so zu sprechen, dass deine Botschaft
            wirklich ankommt.
          </p>
          <p className="mt-2">{explainerText}</p>
          <Link
            to="/pricing"
            data-testid={`${testid}-explainer-cta`}
            className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[var(--fts-primary-bright)] hover:underline"
          >
            Mehr erfahren — Pro Solo starten →
          </Link>
        </div>
      )}

      <div
        data-testid={`${testid}-text`}
        className="text-lg leading-relaxed text-stone-900 space-y-2 dark:text-white"
      >
        {saetze.map((s, i) => {
          const isClimax = !!s.climax;
          return (
            <span
              key={i}
              data-testid={`${testid}-satz-${i}`}
              data-anker={(s.anker || []).join(",")}
              data-climax={isClimax ? "true" : "false"}
              className={isClimax ? "font-semibold" : ""}
              style={isClimax ? { color: climaxColor } : undefined}
            >
              {s.text}
              {i < saetze.length - 1 ? " " : ""}
            </span>
          );
        })}
      </div>

      <div className="mt-4 flex items-center justify-between gap-2">
        <button
          type="button"
          data-testid={`${testid}-copy`}
          onClick={handleCopy}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-md border border-stone-300 bg-white hover:bg-stone-50 text-stone-800 transition-colors dark:border-slate-600 dark:bg-slate-900 dark:hover:bg-slate-800 dark:text-slate-100"
        >
          {copied ? (
            <>
              <Check className="w-4 h-4 text-emerald-600" /> Kopiert
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" /> Kopieren
            </>
          )}
        </button>
        {/* Sprint 6 BLOCKER 2 AC #3 — Word-Counter mit Soft-Limit-Warnung */}
        <span
          data-testid={`${testid}-word-counter`}
          className={`text-xs tabular-nums font-medium ${softWarning ? "text-amber-700" : "text-stone-500"}`}
        >
          {wordCount} / {hardLimit} Wörter
          {softWarning ? " (etwas lang)" : ""}
        </span>
      </div>

      {bridge.subtext && (
        <div className="mt-5 pt-5 border-t border-stone-100">
          <p
            data-testid={`${testid}-subtext`}
            className="text-sm italic text-stone-600 leading-relaxed"
          >
            {bridge.subtext}
          </p>
        </div>
      )}
    </section>
  );
}

export default BrueckensatzBlock;
